import React, { useEffect, useRef, useState } from 'react';
import {
    Dimensions,
    Keyboard,
    Platform,
    StatusBar,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    TouchableWithoutFeedback,
    View
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import moment_tz from "moment-timezone";
import { Icon } from "react-native-elements";
import LiveClock from "../components/LiveClock";
import DateTimePickerModal from "react-native-modal-datetime-picker";

import {
    getBookModalVisibility,
    getComponentColor,
    getConfig,
    getConnectionStatus,
    getLanguage,
    getLoading,
    getSelectedInterval,
    setActionAuthentication,
    setBookModalVisibility,
    setError,
    setLoading,
    setSuccess
} from "../reducers/configSlice";
import { BookingIntervalContainer } from "../components/BookingIntervalSelector";
import { apiAuthorizationToken, apiSaveBooking } from "../api/actions";
import Notification from "../components/Notification";
import Loader from "../components/Loader";
import { emitFutureBookingList } from "../services/socket/socket";
import { addActionAuthenticationListener } from "../services/actionAuthentication/actionAuthenticationService";
import { CREATE_ACTION, CREATE_AND_CHECKIN_ACTION } from "../constants/default";
import AuthenticationModal from "./AuthenticationModal";
import { FONT_FAMILY, FONT_FAMILY_BOLD, FONT_FAMILY_LIGHT } from "../constants/styleConstants";
import * as NavigationBar from "expo-navigation-bar";
import { isAndroid } from "../services/helpers/getPlatformType";
import { actuatedNormalize, proportionalSize } from "../services/helpers/stylingHelpers";
import { isIntervalDisabled } from "../services/helpers/bookingsHelpers";

const BookModal = () => {
    const dispatch = useDispatch();

    const connectionStatus = useSelector(getConnectionStatus);
    const bookModalVisible = useSelector(getBookModalVisibility);
    const config = useSelector(getConfig);
    const selectedInterval = useSelector(getSelectedInterval);
    const loading = useSelector(getLoading);

    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [bookingTitle, setBookingTitle] = useState(translate('Booking'));
    const [bookingDate, setBookingDate] = useState(moment().format('YYYY-MM-DD'));
    const [customModal, setCustomModal] = useState(false);
    const [keyboardHeight, setKeyboardHeight] = useState(0);

    const [width, setWidth] = useState(Dimensions.get('screen').width);
    const [height, setHeight] = useState(Dimensions.get('screen').height);

    const onLayout = () => {
        setWidth(Dimensions.get('screen').width);
        setHeight(Dimensions.get('screen').height);
    };

    const changeDate = (selectedDate) => {
        const newDateFormatted = moment(selectedDate).format('YYYY-MM-DD');

        if (bookingDate !== newDateFormatted) {
            dispatch(setLoading(true));
            emitFutureBookingList(newDateFormatted);

            setBookingDate(newDateFormatted);
        }
    };

    const closeModal = () => {
        setButtonDisabled(true);

        if (bookingDate !== moment_tz.tz(config.timezone).format('YYYY-MM-DD')) {
            dispatch(setLoading(true));
            emitFutureBookingList(moment_tz.tz(config.timezone).format('YYYY-MM-DD'));
        }

        setBookingDate(moment().format('YYYY-MM-DD'));
        setBookingTitle(translate("Booking"));
        dispatch(setBookModalVisibility(false));

        setTimeout(() => {
            setButtonDisabled(false);
        }, 250);
    };

    const saveBooking = async () => {
        const data = {
            space_id: config.space_id,
            title: bookingTitle,
        };

        data.dates = [{
            start: bookingDate + ' ' + selectedInterval[0],
            end: selectedInterval[1] === "00:00" ? (moment(bookingDate).add(1, 'days').format('YYYY-MM-DD') + ' 00:00') : (bookingDate + ' ' + selectedInterval[1])
        }];

        if (config.add_conference_link) {
            data.conference_type = config.add_conference_link;
        }

        if (config.require_login_actions) {
            let action = CREATE_ACTION;

            if (config.has_checkin) {
                const timezone = config.timezone;
                const currentMoment = moment().tz(timezone);

                const startMoment = moment_tz.tz(data.dates[0].start, timezone).utc().tz(timezone);
                const endMoment = moment_tz.tz(data.dates[0].end, timezone).utc().tz(timezone);

                if (currentMoment.isBetween(startMoment, endMoment) && currentMoment.diff(startMoment, 'minutes') < config.grace_period) {
                    action = CREATE_AND_CHECKIN_ACTION;
                }
            }

            data.terms_and_conditions = 1;

            const result = await apiAuthorizationToken(action, data);

            if (result.status === 1 && result.code && result.data) {
                const authAction = {
                    action: action,
                    successMessage: translate("Booking successfully created"),
                    link: result.data,
                    code: result.code,
                };

                await addActionAuthenticationListener(authAction, closeModal);
                dispatch(setActionAuthentication(authAction));
            }
        } else {
            const { status, error } = await apiSaveBooking(data);

            if (!error) {
                closeModal();
                dispatch(setSuccess(translate('Booking successfully created')));
            } else {
                dispatch(setError(error));
            }
        }
    };

    useEffect(() => {
        if (isAndroid()) {
            NavigationBar.setVisibilityAsync("hidden");
        }
    }, []);

    useEffect(() => {
        if (bookModalVisible) {
            setBookingTitle(translate("Booking"));
        }
    }, [bookModalVisible]);

    useEffect(() => {
        setButtonDisabled(!bookingTitle || loading || !connectionStatus);
    }, [bookingTitle, loading, connectionStatus]);

    useEffect(() => {
        const keyboardDidShowListener = Keyboard.addListener(
            'keyboardDidShow',
            (e) => {
                setKeyboardHeight(e.endCoordinates.height);
            }
        );
        const keyboardDidHideListener = Keyboard.addListener(
            'keyboardDidHide',
            (e) => {
                setKeyboardHeight(0);
            }
        );

        return () => {
            keyboardDidHideListener.remove();
            keyboardDidShowListener.remove();
        };
    }, []);

    if (!bookModalVisible) return null; // Don't render if the modal is not visible

    return (
        <TouchableWithoutFeedback onPress={closeModal}>
            <View style={[styles.overlay, (loading || customModal) && { backgroundColor: 'rgba(0,0,0, 0.6)' }]}>
                <TouchableWithoutFeedback onPress={() => { }}>
                    <View style={[styles.container, Platform.OS === 'android' && { marginBottom: -keyboardHeight }]} onLayout={onLayout}>
                        <Header onClose={closeModal} btnDisabled={buttonDisabled || customModal} blur={customModal} />
                        <View style={styles.content}>
                            <BookingTitleContainer disabled={loading || customModal} value={bookingTitle} onChange={setBookingTitle} blur={customModal} />
                            <DatePickerContainer disabled={loading || customModal} onDateChange={changeDate} date={bookingDate} blur={customModal} />
                            <BookingIntervalContainer date={bookingDate} setBlurEffect={setCustomModal} />
                        </View>
                        <Footer onPress={saveBooking} btnDisabled={buttonDisabled || customModal || selectedInterval.length !== 2 || (moment().format('YYYY-MM-DD') === moment(bookingDate).format('YYYY-MM-DD') && isIntervalDisabled(bookingDate, selectedInterval[0], selectedInterval[1]))} blur={customModal} />
                    </View>
                </TouchableWithoutFeedback>
                <Loader />
                <Notification />
                <AuthenticationModal />
            </View>
        </TouchableWithoutFeedback>
    );
};

const styles = StyleSheet.create({
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    },
    container: {
        flex: 1,
        width: '100%'
    },
    content: {
        flex: 0.635,
        justifyContent: 'space-around',
        marginLeft: '20%',
        marginRight: '20%',
    },
    blurEffect: {
        opacity: 0.3,
    },
    inputTitle: {
        fontFamily: FONT_FAMILY_LIGHT,
        fontSize: actuatedNormalize(18),
        color: '#FFFFFF',
    },
});

const BookingTitleContainer = ({ value, onChange, disabled, blur }) => {
    const dispatch = useDispatch();
    const componentColor = useSelector(getComponentColor);
    const textInput = useRef();

    return (
        <TouchableWithoutFeedback onPress={() => textInput.current.focus()}>
            <View style={[titleStyles.bookingTitleContainer, blur && styles.blurEffect, { backgroundColor: Color(componentColor).alpha(0.06).rgb().string(), borderColor: Color(componentColor).alpha(0.4).rgb().string() }]}>
                <Text style={styles.inputTitle}>
                    {translate("Booking Title")}
                </Text>
                <TextInput
                    ref={textInput}
                    style={titleStyles.bookingTitleInput}
                    underlineColorAndroid="transparent"
                    placeholderTextColor="#93A4AB"
                    autoCapitalize="none"
                    value={value}
                    onChangeText={onChange}
                    editable={!disabled}
                />
            </View>
        </TouchableWithoutFeedback>
    );
};

const DatePickerContainer = ({ onDateChange, date, disabled, blur }) => {
    const [pickerVisible, setPickerVisible] = useState(false);
    const componentColor = useSelector(getComponentColor);
    const language = useSelector(getLanguage);
    const config = useSelector(getConfig);

    const togglePicker = () => {
        if (!disabled) {
            setPickerVisible(!pickerVisible);
        }
    };

    const onConfirm = (selectedDate) => {
        setPickerVisible(false);
        onDateChange(selectedDate);
    };

    return (
        <TouchableWithoutFeedback onPress={togglePicker}>
            <View style={[datePickerStyles.dateContainer, blur && styles.blurEffect, { backgroundColor: Color(componentColor).alpha(0.06).rgb().string(), borderColor: Color(componentColor).alpha(0.4).rgb().string() }]}>
                <View style={datePickerStyles.pickerContainer}>
                    <Text style={styles.inputTitle}>
                        {translate("Booking Date")}
                    </Text>
                    <DateTimePickerModal
                        isVisible={pickerVisible}
                        mode="date"
                        onConfirm={onConfirm}
                        onCancel={togglePicker}
                        date={moment(date).toDate()}
                    />
                    <Text style={datePickerCustomStyles.dateText}>
                        {moment(date).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD') ? translate("Today") : moment(date).format(config.date_format)}
                    </Text>
                </View>
            </View>
        </TouchableWithoutFeedback>
    );
};

const Header = ({ onClose, btnDisabled, blur }) => {
    const config = useSelector(getConfig);

    return (
        <View style={[headerStyles.header, blur && styles.blurEffect]}>
            <TouchableWithoutFeedback onPress={onClose} disabled={btnDisabled}>
                <View style={headerStyles.backButtonContainer}>
                    <Icon size={proportionalSize(40)} name="left" type="antdesign" color="#E3E3E3" disabledStyle={headerStyles.xButtonDisabled} />
                </View>
            </TouchableWithoutFeedback>

            <View style={headerStyles.detailsContainer}>
                <Text style={headerStyles.companyName}>
                    {config && config.title}
                </Text>
            </View>

            <View style={headerStyles.clockContainer}>
                <LiveClock style={headerStyles.clock} />
            </View>
        </View>
    );
};

const Footer = ({ onPress, btnDisabled, blur }) => {
    const componentColor = useSelector(getComponentColor);

    return (
        <View style={[footerStyles.footer, blur && styles.blurEffect]}>
            <TouchableOpacity onPress={onPress} disabled={btnDisabled} style={[footerStyles.button, { backgroundColor: Color(componentColor).rotate(20).rgb().string() }, btnDisabled && { opacity: 0.5 }]}>
                <Text style={footerStyles.buttonText}>
                    {translate("Save booking").toUpperCase()}
                </Text>
            </TouchableOpacity>
        </View>
    );
};

const headerStyles = StyleSheet.create({
    header: {
        flex: 0.165,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    backButtonContainer: {
        flex: 0.2,
        height: '100%',
        justifyContent: 'center',
    },
    xButtonDisabled: {
        backgroundColor: 'rgba(0,0,0,0)',
        opacity: 0.1,
    },
    detailsContainer: {
        flex: 0.6,
    },
    companyName: {
        color: 'white',
        fontFamily: FONT_FAMILY_BOLD,
        fontSize: actuatedNormalize(23),
    },
    clockContainer: {
        flex: 0.2,
    },
    clock: {
        alignSelf: 'center',
    },
});

const footerStyles = StyleSheet.create({
    footer: {
        flex: 0.2,
        marginLeft: '20%',
        marginRight: '20%',
    },
    button: {
        height: '51%',
        width: '32.5%',
        marginTop: 20,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 2.5,
    },
    buttonText: {
        color: 'white',
        fontFamily: FONT_FAMILY,
        fontSize: actuatedNormalize(16),
        textAlign: 'center',
    },
});

const datePickerStyles = StyleSheet.create({
    dateContainer: {
        flex: 0.20,
        flexDirection: 'row',
        paddingLeft: '7.5%',
        paddingRight: '7.5%',
        justifyContent: 'center',
        borderWidth: 1,
        borderRadius: 5,
    },
    pickerContainer: {
        flex: 1,
        justifyContent: 'center',
    },
});

const datePickerCustomStyles = StyleSheet.create({
    dateText: {
        color: '#FFFFFF',
        textAlign: 'left',
        fontFamily: FONT_FAMILY,
        fontSize: actuatedNormalize(18),
    },
});

const titleStyles = StyleSheet.create({
    bookingTitleContainer: {
        flex: 0.20,
        borderWidth: 1,
        borderRadius: 5,
        justifyContent: 'center',
        paddingLeft: '7.5%',
        paddingRight: '7.5%',
    },
    bookingTitleInput: {
        fontSize: actuatedNormalize(18),
        fontFamily: FONT_FAMILY_LIGHT,
        color: '#93A4AB',
        marginLeft: 0,
    },
});

export default BookModal;
